import React from 'react';
import styled from 'styled-components';
import Inventory, { Channel, InventoryProduct } from '../models/Inventory';
import { CloseIcon } from '../Components/Icons';
import { AlertIcon } from '../Components/Icons';
import Button from '../Components/Button';
import { RECEIVER_ACTIONS, SENDER_ACTIONS } from '../models/Engine';
import { PageType } from '../models/PageData';

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  grid-gap: 24px;
  height: 100%;
  overflow: hidden;
  position: relative;
  h1 {
    font-size: 32px;
    text-align: center;
  }
  .inventory-wrapper {
    padding: 48px;
    overflow-y: auto;
    flex-grow: 1;
  }
  .inventory {
    margin-top: 48px;
    display: flex;
    flex-direction: column;
    grid-gap: 12px;
    .row {
      display: flex;
      grid-gap: 12px;
      flex-wrap: wrap;
      border-bottom: 4px solid black;
      padding-bottom: 24px;
      margin-bottom: 12px;
    }
  }
  .products {
    padding: 48px;
    margin-top: 48px;
    display: grid;
    grid-gap: 16px;
    overflow-y: scroll;
  }
  .action-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 16px;
    padding: 0 48px 48px 48px;
    position: relative;
    z-index: 100;
    button {
      width: 100%;
      font-size: 24px;
      padding: 18px 4px;
    }
  }
`;

const ChannelItem = styled.div<{ selected: boolean }>`
    width: 160px;
    position: relative;
    min-height: 180px;
    border: ${(props) => (props.selected ? '2px solid blue' : '1px solid grey')};
    border-radius: 8px;
    .inactive {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 80px;
        height: 80px;
        transform: translate(-50%, -50%);
        color: red;
        z-index: 5;
    }
    .error-channel {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 80px;
        height: 80px;
        transform: translate(-50%, -50%);
        color: #eed202;
        z-index: 5;
    }
    .identifier {
        position: absolute;
        top: 12px;
        left: 12px;
        font-size: 24px;
        font-weight: 600;
        padding: 0;
        margin: 0;
    }
    .info-section {
        padding: 16px 8px;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
        display: grid;
        grid-template-columns: 1fr 40px;
        grid-gap: 12px;
        height: 100%;
        align-items: flex-end;
        .title {
            font-size: 20px;
            font-weight: 600;
            padding; 0;
            margin: 0;
        }
        .quantity {
            font-size: 20px;
            font-weight: 800;
            padding; 0;
            margin: 0;
        }
    }
`;
const ProductItem = styled.div<{ selected: boolean }>`
    border: ${(props) => (props.selected ? '2px solid blue' : '1px solid grey')};
    border-radius: 8px;
    padding: 8px 12px;
        .title {
            font-size: 20px;
            font-weight: 600;
            padding: 0;
            margin: 0;
        }
        .quantity-container {
            margin-top: 4px;
            display: flex;
            grid-gap: 12px;
            input {
                padding: 8px 24px;
                font-size: 24px;
                margin-top: 12px;
                width: 120px;
            }
        }
        button {
            font-size: 20px;
        }
    }
`;

const InventoryMap: React.FC = () => {
  const products = (window.DATA?.products as InventoryProduct[]) ?? [];
  const inventoryData = (window.DATA?.inventory as Inventory) ?? [];
  const [inventory, setInventory] = React.useState<Inventory>(inventoryData);
  const [selectedChannel, setSelectedChannel] = React.useState<Channel>();
  const [showProducts, setShowProducts] = React.useState(false);

  const handleClick = (channel: Channel) => {
    setSelectedChannel(channel);
    setShowProducts(true);
  };

  const handleProductSelect = () => {
    const temp = [...inventory];
    temp.forEach((el, rowIndex) => {
      el.forEach((channel, colIndex) => {
        if (channel.id === selectedChannel?.id) {
          temp[rowIndex][colIndex] = {...selectedChannel} as Channel;
        }
      });
    });
    setSelectedChannel(undefined);
    setShowProducts(false);
    setInventory([...temp]);
  };

  const createPayload = (twoDArray: Channel[][]): string => {
    const oneDList: Channel[] = [];
    for (const row of twoDArray) {
      for (const element of row) {
        oneDList.push(element);
      }
    }
    return JSON.stringify(oneDList);
  };

  const handleSubmit = () => {
    const parseReq = createPayload(inventory);
    window.SENDER({ type: SENDER_ACTIONS.POST_INVENTORY, payload: parseReq });
    window.RECEIVER({ type: RECEIVER_ACTIONS.CHANGE_PAGE, payload: { page: PageType.SCREEN_DEFAULT, message: 'UPDATING INVENTORY' } });
  };

  if (showProducts && selectedChannel) {
    return <Wrapper>
      <h1>
        {selectedChannel.product ? 'CHANGE ' : 'ASSIGN '}PRODUCT {`[${selectedChannel.identifier}]`}
      </h1>
      <div className="inventory-wrapper">
        <div className="products">
          {products.map((product) => (
            <ProductItem
              key={product.id}
              onClick={() => setSelectedChannel({ ...selectedChannel, product: product })}
              selected={selectedChannel?.product?.id === product.id}>
              {selectedChannel?.product?.id === product.id ? (
                <>
                  <p className="title">ENTER QUANTITY</p>
                  <div className="quantity-container">
                    <input
                      type="number"
                      value={selectedChannel.inventoryCount}
                      onChange={(e) =>
                        setSelectedChannel({
                          ...selectedChannel,
                          inventoryCount: parseInt(e.target.value),
                        })
                      }
                    />
                    <Button onClick={handleProductSelect} disabled={isNaN(selectedChannel.inventoryCount)}>
                      ADD PRODUCT
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="title">{product.name}</p>
                  <p className="description">{product.description ?? ''}</p>
                  <p className="description">{product.price}</p>
                </>
              )}
            </ProductItem>
          ))}
          {products.length === 0 && <p className="title">NO PRODUCTS AVAILABLE</p>}
        </div>
      </div>
      <div className="action-container">
        <Button onClick={() => setShowProducts(false)} className="w-full mt-3">
          BACK
        </Button>
      </div>
    </Wrapper>;
  }
  return (
    <Wrapper>
      <h1>INVENTORY</h1>
      <div className="inventory-wrapper">
        <div className="inventory">
          {inventory.map((el, index) => (
            <div key={index} className="row">
              {el.map((channel) => (
                <ChannelItem
                  key={channel.id}
                  onClick={() => channel.isActive && handleClick(channel)}
                  selected={selectedChannel ? selectedChannel.identifier === channel.identifier : false}>
                  <p className="identifier">[{channel.identifier}]</p>
                  {!channel.isActive ? (
                    <>{channel.product ? <AlertIcon className="error-channel" /> : <CloseIcon className="inactive" />}</>
                  ) : (
                    <>
                      <div className="info-section">
                        {channel.product ? (
                          <>
                            <p className="title">{channel.product.name}</p>
                            <p className="quantity">{channel.inventoryCount}</p>
                          </>
                        ) : (
                          <p className="title" style={{ color: 'blue' }}>
                            Assign Product
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </ChannelItem>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="action-container">
        <Button onClick={() => window.RECEIVER({ type: RECEIVER_ACTIONS.CHANGE_PAGE, payload: { page: PageType.SCREEN_ADMIN } })}>BACK</Button>
        <Button onClick={handleSubmit}>SUBMIT</Button>
      </div>
    </Wrapper>
  );
};
export default InventoryMap;
